import axios from "axios";
import Moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { MdOutlineVisibilityOff, MdVisibility } from "react-icons/md";
import Actions from "../../components/Common/Actions";
import DoctorsFilterOptions from "../../components/Common/CommonFilterOptions";
import SelectComponent from "../../components/Common/Select";
import {
  AgentName,
  BookingActions,
  getAgentStatus,
  getWebBookingActionStatus,
  getWebBookingStatus,
  webBookingProfileStatus,
} from "../../components/Constants";
import ListPageLayout from "../../components/Layout";
import AssignedTo from "../../components/Modal/AssignedTo";
import Deletepopup from "../../components/Modal/Deletepopup";
import LeadStatus from "../../components/Modal/LeadStatus";
import SucessModal from "../../components/Modal/SucessModal";
import {
  formatDateTime,
  updatedAgentStatus,
  updatedBookingAction,
  updatedBookingStatus,
} from "../../components/Utils";
import useDebouncedSearch from "../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../components/Utils/hooks/useSortHandler";
import "./webBooking.scss";

const sensitiveReasons = [
  "Sexologists",
  "Andrologists",
  "Ayurvedic Sexologists",
];

const AboutSection = ({ specialization, about }) => {
  const [visibleAbout, setVisibleAbout] = useState(false);
  const isSensitive = sensitiveReasons.includes(specialization);

  if (!isSensitive) return <>{about || "-"}</>;

  return visibleAbout ? (
    <>
      <MdVisibility
        style={{ cursor: "pointer", marginRight: "5px" }}
        onClick={() => setVisibleAbout(false)}
      />
      {about || "-"}
    </>
  ) : (
    <MdOutlineVisibilityOff
      style={{ cursor: "pointer" }}
      onClick={() => setVisibleAbout(true)}
    />
  );
};

const Symptoms = ({ specialization, symptoms }) => {
  const isHide = sensitiveReasons.includes(specialization);
  const [visibleSymptoms, setVisibleSymptoms] = useState(false);

  const toggleVisibility = () => {
    setVisibleSymptoms((prev) => !prev);
  };

  return !isHide ? (
    <>{symptoms?.join(", ")}</>
  ) : !visibleSymptoms ? (
    <MdOutlineVisibilityOff
      style={{ cursor: "pointer", alignItems: "center" }}
      onClick={() => toggleVisibility()}
    />
  ) : (
    <>
      {
        <MdVisibility
          style={{
            cursor: "pointer",
            marginRight: "5px",
          }}
          onClick={() => toggleVisibility()}
        />
      }
      {symptoms?.join(", ")}
    </>
  );
};

const WebBookingInClinicConsultation = () => {
  const [sort, setSort] = useState("newest");
  const [sortName, setSortName] = useState("Newest First");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }))
  );
  const [loader, setLoader] = useState(true);

  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [assignModal, setAssignModal] = useState({ id: "", status: false });
  const [leadStatusModal, setLeadStatusModal] = useState({
    id: "",
    status: false,
  });
  const [success, setSuccess] = useState(false);
  const [leadStatussuccess, setLeadStatusSuccess] = useState(false);

  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}inclinic-booking-details/list`,
      initialParams: { sort, searchText: search },
    });

  const handleSortClick = useSortHandler(setSort, setSortName);
  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "inclinic-booking-details",
    fetchData,
    setShowDelete
  );

  useEffect(() => {
    setLoader(true)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search]);

  const updateStatus = (url, id, value, updateFn) => {
    setLoader(true)
    axios
      .patch(
        `${process.env.REACT_APP_API_URL}${url}/update/${id}`,
        updateFn(value)
      )
      .then(fetchData);
  };

  const successModalClose = () => {
    setSuccess(false);
    setLeadStatusSuccess(false);
    fetchData();
  };

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        dataIndex: "index",
        render: (_, __, index) => index + 1,
        fixed: "left",
      },
      {
        title: "Patient Name",
        dataIndex: "name",
        fixed: "left",
      },
      {
        title: "Created At",
        dataIndex: "createdAt",
        render: (text) => Moment(text).format("DD-MM-YYYY LT"),
        width: 220,
      },
      {
        title: "Last updated on",
        dataIndex: "updatedAt",
        render: (text) => Moment(text).format("DD-MM-YYYY"),
        width: 220,
      },
      {
        title: "Doctor Name",
        dataIndex: "doctor_name",
        fixed: "left",
      },
      {
        title: "Doctor Specializations",
        dataIndex: "doctor_primary_specialization",
      },
      {
        title: "Doctor Mobile No",
        dataIndex: "doctor_mobile",
      },
      {
        title: "Patient Mobile No",
        dataIndex: "mobile",
      },
      {
        title: "Patient Email id",
        dataIndex: "email",
      },
      {
        title: "Age",
        dataIndex: "age",
      },
      {
        title: "Gender",
        dataIndex: "gender",
      },
      {
        title: "Relation",
        dataIndex: "relation",
      },
      {
        title: "Health Conditions",
        dataIndex: "symptoms",
        render: (symptoms, record) => (
          <Symptoms
            about={symptoms}
            specialization={record.doctor_primary_specialization}
          />
        ),
      },
      {
        title: "About",
        dataIndex: "about",
        render: (about, record) => (
          <AboutSection
            about={about}
            specialization={record.doctor_primary_specialization}
          />
        ),
      },
      {
        title: "Received At",
        dataIndex: "createdAt",
        render: (date) => Moment(date).format("MMMM DD, YYYY LT"),
      },
      {
        title: "Booking Requested Date & Time",
        dataIndex: "date",
        render: (_, record) => {
          return formatDateTime(record.date, record.time);
        },
      },
      {
        title: "Action",
        dataIndex: "_id",
        render: (id, record) => (
          <SelectComponent
            props={{
              Array: webBookingProfileStatus,
              value: getWebBookingStatus(record),
              style: { color: "#CB1B5B", width: "200px" },
              onChange: (value) =>
                updateStatus(
                  "inclinic-booking-details",
                  record._id,
                  value,
                  updatedBookingStatus
                ),
            }}
          />
        ),
      },
      {
        title: "Booking Action",
        dataIndex: "bookingAction",
        render: (_, record) => (
          <SelectComponent
            props={{
              Array: BookingActions,
              value: getWebBookingActionStatus(record),
              style: { color: "#CB1B5B", width: "200px" },
              onChange: (value) => {
                if (value === "Delete") {
                  setDeleteId(record._id);
                  setShowDelete(true);
                } else {
                  updateStatus(
                    "inclinic-booking-details",
                    record._id,
                    value,
                    updatedBookingAction
                  );
                }
              },
            }}
          />
        ),
      },
      {
        title: "Agent Name",
        dataIndex: "agentName",
        render: (_, record) => (
          <SelectComponent
            props={{
              Array: AgentName,
              value: getAgentStatus(record),
              style: { color: "#CB1B5B", width: "200px" },
              onChange: (value) =>
                updateStatus(
                  "inclinic-booking-details",
                  record._id,
                  value,
                  updatedAgentStatus
                ),
            }}
          />
        ),
      },
      {
        title: "Delete Action",
        dataIndex: "delete",
        render: (_, record) => (
          <Actions
            isDelete={true}
            onClickDelete={() => {
              setDeleteId(record._id);
              setShowDelete(true);
            }}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={<></>}
      addButton={<></>}
      titles={<></>}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination({
              ...pagination,
              limit: val,
              skip: 0,
              currentPage: 1,
            })
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource: records.map((el) => ({ ...el, key: el._id })),
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
      {assignModal.status && (
        <AssignedTo
          id={assignModal.id}
          setAssignModal={setAssignModal}
          setSuccess={setSuccess}
        />
      )}
      {success && (
        <SucessModal name="BD" post="Assigned" onClick={successModalClose} />
      )}
      {leadStatusModal.status && (
        <LeadStatus
          id={leadStatusModal.id}
          setLeadStatusModal={setLeadStatusModal}
          setLeadStatusSuccess={setLeadStatusSuccess}
        />
      )}
      {leadStatussuccess && (
        <SucessModal
          name="Lead Status"
          post="Updated"
          onClick={successModalClose}
        />
      )}
    </ListPageLayout>
  );
};

export default WebBookingInClinicConsultation;
